import React, { useEffect, useState, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Stack, FormControl, InputLabel, Select, MenuItem, Box, Typography, IconButton, Paper } from '@mui/material';
import { Upload, Delete, CloudUpload, Close } from '@mui/icons-material';
import { fetchCurrentUser } from '../services/auth';
import { listPayslips, createPayslip, getPayslip, deletePayslip } from '../services/payslips';

const PayslipsPage = () => {
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ file_paths: [], slip_date: '' });
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const isAdminOrComptable = me?.role === 1 || me?.role_id === 1 || me?.role === 3 || me?.role_id === 3;
  const employeeId = me?.employee_id;
  const targetEmployeeId = (me && (me.role === 1 || me.role_id === 1 || me.role === 3 || me.role_id === 3))
    ? (selectedEmployeeId || '')
    : employeeId;

  const load = async () => {
    if (!targetEmployeeId) return;
    const res = await listPayslips(targetEmployeeId);
    setRows(res.data);
  };

  useEffect(() => {
    (async () => {
      const user = await fetchCurrentUser();
      setMe(user);
    })();
  }, []);

  // Load employees for selector if admin/comptable
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!(me?.role === 1 || me?.role_id === 1 || me?.role === 3 || me?.role_id === 3)) return;
      const api = (await import('../services/api')).default;
      const res = await api.get('/employees');
      setEmployees(res.data || []);
      // Sort employees alphabetically by first_name + last_name
      const sortedEmployees = (res.data || []).sort((a, b) => {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setEmployees(sortedEmployees);
      // Default to first employee if none selected
      if (!selectedEmployeeId && sortedEmployees.length) {
        setSelectedEmployeeId(sortedEmployees[0].id);
      }
    };
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.role, me?.role_id]);

  // Load payslips when target employee changes
  useEffect(() => { if (targetEmployeeId) load(); /* eslint-disable-next-line */ }, [targetEmployeeId]);

  const onOpen = () => setOpen(true);
  const onClose = () => {
    setOpen(false);
    setForm({ file_paths: [], slip_date: '' });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(file => {
        fd.append('files', file);
      });
      
      const res = await (await import('../services/api')).default.post('/upload', fd, { 
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const paths = Array.isArray(res.data.filePaths) ? res.data.filePaths : [res.data.filePaths];
      setForm(f => ({ ...f, file_paths: [...f.file_paths, ...paths] }));
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setForm(f => ({ 
      ...f, 
      file_paths: f.file_paths.filter((_, i) => i !== index) 
    }));
  };

  const onSave = async () => {
    if (!targetEmployeeId || form.file_paths.length === 0) return;
    
    // Create multiple payslips if multiple files
    const promises = form.file_paths.map(file_path => 
      createPayslip(targetEmployeeId, { 
        file_path, 
        slip_date: form.slip_date || new Date().toISOString().split('T')[0] // Auto-date if not provided
      })
    );

    try {
      await Promise.all(promises);
      setOpen(false);
      setForm({ file_paths: [], slip_date: '' });
      await load();
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const download = async (id) => {
    const res = await getPayslip(id);
    const url = res.data.file_path.startsWith('http') ? res.data.file_path : `${process.env.REACT_APP_API_URL?.replace('/api','') || ''}${res.data.file_path}`;
    window.open(url, '_blank');
  };

  const onDelete = async (id) => {
    await deletePayslip(id);
    await load();
  };

  const columns = [
    { field: 'slip_date', headerName: 'Date', width: 160, headerClassName: 'super-app-theme--header' },
    { field: 'file_path', headerName: 'Fichier', flex: 1, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions', headerName: 'Actions', width: 220, headerClassName: 'super-app-theme--header', renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
          <Button size="small" onClick={() => download(params.row.id)}>Télécharger</Button>
          {isAdminOrComptable && (
            <Button color="error" size="small" onClick={() => onDelete(params.row.id)}>Supprimer</Button>
          )}
        </Stack>
      )
    }
  ];

  return (
    <div>
      <h2>Fiches de paie</h2>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        {isAdminOrComptable && (
          <>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Employé</InputLabel>
              <Select label="Employé" value={selectedEmployeeId} onChange={(e) => setSelectedEmployeeId(e.target.value)}>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={onOpen} disabled={!selectedEmployeeId} startIcon={<CloudUpload />}>
              Ajouter des fiches
            </Button>
          </>
        )}
      </Stack>
      <div style={{ height: 420 }}>
        <DataGrid 
          rows={rows} 
          columns={columns} 
          getRowId={(r) => r.id} 
          pageSize={10}
          getRowHeight={() => 80}
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderBottom: '2px solid #1565c0',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              padding: '16px 8px',
              borderBottom: '1px solid #f0f0f0',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
              '&:hover': {
                backgroundColor: '#e3f2fd',
              },
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-pagination': {
              '& .MuiIconButton-root': {
                borderRadius: '4px',
              },
            },
          }}
        />
      </div>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Ajouter des fiches de paie</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Date optionnelle */}
            <TextField
              fullWidth
              type="date"
              label="Date (optionnelle)"
              name="slip_date"
              value={form.slip_date}
              onChange={(e) => setForm({ ...form, slip_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Laissez vide pour utiliser la date du jour"
            />
            
            {/* Zone de drag & drop */}
            <Paper
              sx={{
                p: 3,
                border: '2px dashed #ccc',
                borderRadius: 2,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragActive ? '#f5f5f5' : '#fafafa',
                transition: 'all 0.3s ease'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Stack spacing={2} alignItems="center">
                <CloudUpload sx={{ fontSize: 48, color: '#ccc' }} />
                <Typography variant="h6" color="textSecondary">
                  {dragActive ? 'Lâchez les fichiers ici' : 'Glissez-déposez les fichiers ici'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ou cliquez pour sélectionner
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Formats acceptés: Images (JPG, PNG, GIF) et PDF
                </Typography>
              </Stack>
            </Paper>

            {/* Liste des fichiers sélectionnés */}
            {form.file_paths.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Fichiers sélectionnés ({form.file_paths.length}):
                </Typography>
                <Stack spacing={1}>
                  {form.file_paths.map((path, index) => (
                    <Paper key={index} sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {path.split('/').pop() || path}
                      </Typography>
                      <IconButton size="small" onClick={() => removeFile(index)} color="error">
                        <Delete />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {uploading && <Alert severity="info">Téléversement en cours…</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button 
            onClick={onSave} 
            disabled={form.file_paths.length === 0 || uploading} 
            variant="contained"
          >
            Enregistrer ({form.file_paths.length} fiche{form.file_paths.length > 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayslipsPage;
